import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { dishes, heroDishes } from "../data/dishes";
import { photos } from "../data/photos";
import { business } from "../data/business";
import { useTheme, useDocumentTitle, useSite } from "../hooks/useSite";
import FoodImage from "../components/FoodImage";
import DishSwitcher from "../components/DishSwitcher";
import Facts from "../components/Facts";
import SpeechBubble from "../components/SpeechBubble";
import { Host, Guest, BenefitIcon, Utensil, Panorama } from "../illustrations";

const benefits = [
  ["Savoury", "cravings", "Burgers, sandwiches, momos and munchies. A café menu built for different moods."],
  ["Sweet", "little breaks", "Ice cream rolls, sundaes, waffle cones and chocolatey shakes. Save a little room for your sweet side."],
  ["Local", "café moments", "Hanamkonda, Warangal. A familiar local setting for an easy break, a celebration or a little time together."],
  ["Simple", "menu discovery", "Explore each dish, pick your mood and order through Swiggy or Zomato when you are ready."],
];

const marqueeWords = ["Ice Cream Rolls", "Burgers", "Shakes", "Momos", "Sundaes", "Sandwiches", "Munchies", "Good times"];

function pickHeroIndex(search) {
  const forced = new URLSearchParams(search).get("hero");
  const n = Number(forced);
  if (forced !== null && Number.isInteger(n) && n >= 0 && n < heroDishes.length) return n;
  return Math.floor(Math.random() * heroDishes.length);
}

export default function HomePage() {
  const location = useLocation();
  // One of four coordinated hero themes is chosen per page load (?hero=0..3 forces one).
  const heroIndex = useMemo(() => pickHeroIndex(location.search), [location.search]);
  const hero = heroDishes[heroIndex];
  const [selected, setSelected] = useState(0);
  const current = dishes[selected];
  const { openModal } = useSite();
  useTheme(hero);
  useDocumentTitle("Good food. Good mood.");

  return (
    <>
      <section className="ro-hero" data-hero={heroIndex}>
        <div className="ro-container ro-hero__grid">
          <div className="ro-hero__copy">
            <h1>
              RollOver is your place for a <strong>cheesy bite, a sweet escape</strong> or a <strong>catch-up with friends.</strong>
            </h1>
            <p className="ro-hero__line ro-outline" aria-hidden="true">
              {hero.cue}
            </p>
            <p className="ro-sr">{hero.cue}</p>
            <Link className="ro-pill" to={`/menu/${hero.id}`}>
              Learn more
            </Link>
          </div>
          <div className="ro-hero__stage">
            <FoodImage dish={hero} animate />
          </div>
        </div>
      </section>

      <section className="ro-showcase" style={{ "--ro-theme": current.colour }} aria-labelledby="ro-showcase-title">
        <div className="ro-container">
          <div id="ro-dish-panel" role="tabpanel" aria-labelledby={`ro-dish-tab-${selected}`} tabIndex={0} key={current.id} className="ro-showcase__panel">
            <h2 id="ro-showcase-title" className="ro-showcase__title">
              <strong className="ro-italic">{current.name}</strong> <span className="ro-outline">{current.suffix}</span>
            </h2>
            <div className="ro-showcase__stage">
              <FoodImage dish={current} />
              <SpeechBubble to={`/menu/${current.id}`} className="ro-showcase__bubble">
                Discover this
                <br />
                dish
              </SpeechBubble>
            </div>
            <p className="ro-showcase__desc">{current.desc}</p>
            <ul className="ro-cues">
              <li>
                <strong>Your</strong> craving
              </li>
              <li>
                <strong>Your</strong> café break
              </li>
              <li>
                <strong>Your</strong> RollOver
              </li>
            </ul>
            <Facts dish={current} />
          </div>
          <DishSwitcher mode="tabs" selected={selected} onSelect={setSelected} panelId="ro-dish-panel" />
        </div>
      </section>

      <section className="ro-texture" aria-label="RollOver ice cream rolls close up" style={{ backgroundImage: `url("${photos.rollsClose}")` }}>
        <small>RollOver photography</small>
      </section>

      <section className="ro-benefits">
        <Utensil className="ro-benefits__utensil" />
        <div className="ro-container">
          <div className="ro-benefits__grid">
            {benefits.map(([a, b, text], i) => (
              <article className="ro-benefit" key={a}>
                <BenefitIcon index={i} />
                <div>
                  <h3>
                    {a} <strong className="ro-italic">{b}</strong>
                  </h3>
                  <p>{text}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="ro-benefits__host">
            <SpeechBubble to="/what-is-rollover">
              Discover the
              <br />
              RollOver mood
            </SpeechBubble>
            <Host className="ro-benefits__host-figure" />
          </div>
        </div>
      </section>

      <section className="ro-story-home">
        <div className="ro-container ro-container--narrow ro-story-home__grid">
          <div className="ro-story-home__figure ro-bob">
            <SpeechBubble to="/our-story" className="ro-story-home__bubble">
              Our story?
              <br />
              Come on in.
            </SpeechBubble>
            <Guest />
          </div>
          <div>
            <h2>
              <span className="ro-outline">RollOver’s</span> <strong className="ro-italic">Story</strong>
            </h2>
            <p>
              {business.slogan}
              <br />
              <br />
              A cheesy bite after a long day. Something sweet with your favourite people. A little café break that turns into a good memory. That is the RollOver feeling, served in Hanamkonda.
            </p>
          </div>
        </div>
        <Panorama className="ro-panorama" />
      </section>

      <section className="ro-availability" aria-labelledby="ro-availability-title">
        <div className="ro-marquee" aria-hidden="true">
          <div className="ro-marquee__track">
            {[...marqueeWords, ...marqueeWords].map((w, i) => (
              <span key={i}>{w}</span>
            ))}
          </div>
        </div>
        <div className="ro-container">
          <h2 id="ro-availability-title">
            <span className="ro-outline ro-outline--light">Find your</span> <strong className="ro-italic">RollOver</strong>
          </h2>
          <p>
            One little café in Hanamkonda, Warangal.
            <br />
            Find the location, or order online through Swiggy and Zomato.
          </p>
          <div className="ro-availability__actions">
            <Link className="ro-pill" to="/find-us">
              Find us
            </Link>
            <button type="button" className="ro-pill" onClick={() => openModal("order")}>
              Order online
            </button>
            <Link className="ro-pill" to="/events">
              Celebrate with us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
